import { useTranslation } from 'react-i18next';
import { useSchema } from 'domain/ClientService';
import { OPTION_LIST, separator } from 'constants/constants';
import { useQuery } from '@tanstack/react-query';
import {
    SortableItemType,
    sortByAlphabet,
    sortList,
    toLowerKeys,
} from '../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/helpers/sortList';
import {
    removeListLanguages,
} from '../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/helpers/removeListLanguages';
import {
    additionalCountryList,
} from '../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/constants/options';

const defaultAdapters = {
    [OPTION_LIST.country]: (list) => {
        const orderedCountriesList = sortByAlphabet(list);

        return sortList([separator, ...(orderedCountriesList as [])], additionalCountryList);
    },
    [OPTION_LIST.gender]: (list) => toLowerKeys(list),
    [OPTION_LIST.maritalStatus]: (list) => (list),
    [OPTION_LIST.language]: (list) => toLowerKeys(removeListLanguages(list)),
    [OPTION_LIST.salutation]: (list) => toLowerKeys(list),
    [OPTION_LIST.salutation]: (list) => toLowerKeys(list),
    [OPTION_LIST.originOfAssets]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.beneficialOwner]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.exclusionSubstitute]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.preferenceExclusion]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.preferenceStocks]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.preferenceInclusion]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
    [OPTION_LIST.fatfGreylist]: (list) => list?.map((item) => ({ ...item, value: `${item.id}` })),
};

export const useOptionList = (
    listKeys = [
        OPTION_LIST.country,
        OPTION_LIST.gender,
        OPTION_LIST.maritalStatus,
        OPTION_LIST.language,
        OPTION_LIST.salutation,
    ],
    adapters = defaultAdapters,
) => {
    const { i18n: { language } } = useTranslation();
    const { getSchemaListByKey } = useSchema();

    const { data, isLoading, error } = useQuery({
        // The queryKey ensures data refreshes when language or keys change
        queryKey: ['optionLists', language, listKeys],
        queryFn: async () => {
            const listsData = await Promise.all(
                listKeys.map((key) => getSchemaListByKey<Array<SortableItemType>>({ key })),
            );

            // Apply adapters to the data
            return listsData.map((listDataItem, index) => {
                const key = listKeys[index];

                return adapters[key] ? adapters[key](listDataItem) : listDataItem;
            });
        },
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
        // Optional: Keep previous data while fetching for a smoother UI transition
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: true,
    });

    return {
        lists: data || [],
        isLoading,
        error,
    };
};
